import React, { useRef, useState, useEffect } from "react";

// LazyImage now accepts:
// - dataSrc: the real image URL to load when the element becomes visible
// - placeholder: an optional placeholder url to show while off-screen
// It will only call onLoad when the real (visible) image finishes loading.
const LazyImage = ({ dataSrc, placeholder, alt, className, onLoad, ...props }) => {
  const imgRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        try {
          observer.unobserve(imgRef.current);
        } catch (e) {
          // ignore
        }
      }
    };
  }, []);

  const handleLoad = (e) => {
    // Only forward onLoad when the real image has been loaded (i.e. when visible)
    if (isVisible && typeof onLoad === "function") {
      onLoad(e);
    }
  };

  const src = isVisible ? dataSrc : (placeholder || dataSrc);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      onLoad={handleLoad}
      {...props} // forward other props (onClick, style, etc.)
    />
  );
};

export default LazyImage;
